// Dates cross the wire as ISO 8601 UTC strings (Python datetime -> JSON string).
// CLAUDE.md Sec.7 requires UTC at the boundary, local TZ only at the render edge.
// The browser's `Intl.DateTimeFormat` resolves to the user's local time zone by
// default and is the documented render-edge boundary for V1.

const NULLISH_DISPLAY = "—";

export interface FormatDateTimeOptions {
  locale?: string;
  fallback?: string;
}

function parseIso(value: string): Date {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`formatDateTime: invalid ISO 8601 string "${value}"`);
  }
  return parsed;
}

export function formatDateTime(
  value: string | null | undefined,
  options: FormatDateTimeOptions = {},
): string {
  const { locale, fallback = NULLISH_DISPLAY } = options;
  if (value === null || value === undefined) {
    return fallback;
  }
  const date = parseIso(value);
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDate(
  value: string | null | undefined,
  options: FormatDateTimeOptions = {},
): string {
  const { locale, fallback = NULLISH_DISPLAY } = options;
  if (value === null || value === undefined) {
    return fallback;
  }
  const date = parseIso(value);
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

// Render only the local wall-clock time (HH:MM:SS, 24-hour) for an ISO
// UTC string — the time half of the Trades-grid Date/Time/Close-Time
// split. Null / undefined render the em-dash so an open trade's
// close-time cell stays blank-but-aligned.
export function formatTime(
  value: string | null | undefined,
  options: FormatDateTimeOptions = {},
): string {
  const { locale, fallback = NULLISH_DISPLAY } = options;
  if (value === null || value === undefined) {
    return fallback;
  }
  const date = parseIso(value);
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

// Like formatTime but drops seconds (HH:MM, 24-hour) — used by the compact
// dashboard "Recent trades" table where seconds are noise.
export function formatTimeShort(
  value: string | null | undefined,
  options: FormatDateTimeOptions = {},
): string {
  const { locale, fallback = NULLISH_DISPLAY } = options;
  if (value === null || value === undefined) {
    return fallback;
  }
  const date = parseIso(value);
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}
